import { useEffect, useState, useCallback } from 'react'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/stores/authStore'
import {
  isPushConfigured,
  isNotificationSupported,
  requestAdminFcmToken,
  subscribeForegroundMessages,
  showBrowserNotification,
} from '@/lib/messaging'
import {
  getPushPlatformInfo,
  pushBlockMessage,
  type PushPlatformInfo,
} from '@/lib/pushPlatform'
import { saveAdminFcmToken } from '@/services/admin/fcmTokens'

const DEFAULT_PLATFORM_INFO: PushPlatformInfo = {
  canEnablePush: false,
  reason: null,
  inAppAlertsAvailable: true,
  isStandalonePwa: false,
}

export function useAdminPushNotifications() {
  const user = useAuthStore((s) => s.user)
  const isAdmin = useAuthStore((s) => s.isAdmin)
  const [enabled, setEnabled] = useState(false)
  const [loading, setLoading] = useState(false)
  const [platform, setPlatform] = useState<PushPlatformInfo>(DEFAULT_PLATFORM_INFO)

  useEffect(() => {
    void getPushPlatformInfo().then(setPlatform)
  }, [])

  const enablePush = useCallback(async () => {
    const info = await getPushPlatformInfo()
    setPlatform(info)

    if (!user?.uid) return false

    if (!info.canEnablePush) {
      toast.error(
        info.reason ? pushBlockMessage(info.reason) : 'Push is not available here',
        { duration: 10000 }
      )
      return false
    }

    if (!isPushConfigured()) {
      toast.error(
        'Push is not configured. Add VITE_FIREBASE_VAPID_KEY to .env and redeploy.'
      )
      return false
    }

    setLoading(true)
    try {
      const token = await requestAdminFcmToken()
      if (!token) {
        toast.error('Notification permission was denied or unavailable')
        return false
      }
      await saveAdminFcmToken(user.uid, user.email || '', token)
      setEnabled(true)
      toast.success('Order alerts enabled on this device')

      await showBrowserNotification(
        'Ooudh Kraft Admin',
        'You will receive alerts when new orders are placed.',
        {}
      )
      return true
    } catch (err) {
      console.error('enablePush failed', err)
      toast.error(
        err instanceof Error ? err.message : 'Could not enable notifications'
      )
      return false
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    if (!isAdmin || !user) return

    let unsubscribe: (() => void) | undefined

    void subscribeForegroundMessages((payload) => {
      const title = payload.title || 'New order placed'
      const body = payload.body || ''
      toast.success(body ? `${title} — ${body}` : title, { duration: 8000 })
    }).then((unsub) => {
      unsubscribe = unsub ?? undefined
    })

    return () => unsubscribe?.()
  }, [isAdmin, user])

  useEffect(() => {
    if (!isAdmin || !user || !isPushConfigured() || !isNotificationSupported()) {
      return
    }

    void (async () => {
      const info = await getPushPlatformInfo()
      setPlatform(info)
      if (!info.canEnablePush) return
      if (Notification.permission !== 'granted') return

      try {
        const token = await requestAdminFcmToken()
        if (token) {
          await saveAdminFcmToken(user.uid, user.email || '', token)
          setEnabled(true)
        }
      } catch (err) {
        console.warn('Auto FCM token refresh failed', err)
      }
    })()
  }, [isAdmin, user])

  return {
    enabled,
    loading,
    enablePush,
    isConfigured: isPushConfigured(),
    platform,
    canEnablePush: platform.canEnablePush,
  }
}
